'use client';

import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';

import { twMerge } from '@/tailwind-merge.config';
import type { UploadFile } from '@/types/service-api';

interface FileUploadDropzoneProps {
  files: UploadFile[];
  onFilesChange: (files: UploadFile[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  className?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.pdf';

export function FileUploadDropzone({
  files,
  onFilesChange,
  disabled = false,
  maxFiles = 10,
  className,
}: FileUploadDropzoneProps) {
  const { t } = useTranslation('common');
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Generate unique ID for files
  const generateFileId = () =>
    `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Validate single file
  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > MAX_FILE_SIZE) {
        return t('complaint.file_upload.errors.file_too_large', {
          name: file.name,
        });
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        return t('complaint.file_upload.errors.invalid_type', {
          name: file.name,
        });
      }
      return null;
    },
    [t]
  );

  // Process added files
  const processFiles = useCallback(
    (newFiles: FileList | File[]) => {
      setValidationError(null);

      const fileArray = Array.from(newFiles);
      const remainingSlots = maxFiles - files.length;

      if (fileArray.length > remainingSlots) {
        setValidationError(
          t('complaint.file_upload.errors.max_files', { max: maxFiles })
        );
        return;
      }

      const validFiles: UploadFile[] = [];
      const errors: string[] = [];

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          errors.push(error);
        } else {
          // Check for duplicates
          const isDuplicate = files.some(
            (f) => f.name === file.name && f.size === file.size
          );
          if (!isDuplicate) {
            validFiles.push({
              id: generateFileId(),
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              status: 'pending',
              progress: 0,
            });
          }
        }
      }

      if (errors.length > 0) {
        setValidationError(errors.join(', '));
      }

      if (validFiles.length > 0) {
        onFilesChange([...files, ...validFiles]);
      }
    },
    [files, maxFiles, onFilesChange, validateFile, t]
  );

  // Drag&Drop Handler
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        processFiles(droppedFiles);
      }
    },
    [disabled, processFiles]
  );

  // File Input Handler
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
        e.target.value = ''; // Reset for re-selection of same file
      }
    },
    [processFiles]
  );

  // Remove file
  const handleRemoveFile = useCallback(
    (fileId: string) => {
      onFilesChange(files.filter((f) => f.id !== fileId));
    },
    [files, onFilesChange]
  );

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={className}>
      {/* Dropzone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={twMerge(
          'relative flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragging ? 'border-black bg-gray-100' : 'border-gray-300 bg-gray-50',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {/* Upload Icon */}
        <svg
          className="mb-4 size-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>

        <p className="mb-2 text-p1CopyMobile font-medium lg:text-p1CopyDesktop">
          {t('complaint.file_upload.drag_drop')}
        </p>
        <p className="mb-4 text-p2CopyMobile text-grau lg:text-p2CopyDesktop">
          {t('complaint.file_upload.or')}
        </p>

        {/* Hidden File Input */}
        <input
          type="file"
          id="file-upload"
          multiple
          accept={ALLOWED_EXTENSIONS}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="hidden"
        />
        <label
          htmlFor="file-upload"
          className={twMerge(
            'btn btn--white cursor-pointer uppercase',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {t('complaint.file_upload.browse')}
        </label>

        <p className="mt-4 text-p2CopyMobile text-grau lg:text-p2CopyDesktop">
          {t('complaint.file_upload.allowed_types')}
        </p>
        <p className="text-p2CopyMobile text-grau lg:text-p2CopyDesktop">
          {t('complaint.file_upload.max_size')}
        </p>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="mt-4 rounded-lg bg-red-50 p-3">
          <p className="text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
            {validationError}
          </p>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-p2CopyMobile font-medium uppercase lg:text-p2CopyDesktop">
            {t('complaint.file_upload.selected_files')} ({files.length})
          </h4>

          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="flex items-center gap-3">
                {/* File Type Icon */}
                <div className="flex size-10 items-center justify-center rounded bg-gray-100">
                  {file.type === 'application/pdf' ? (
                    <span className="text-xs font-bold text-red-600">PDF</span>
                  ) : (
                    <span className="text-xs font-bold text-blue-600">IMG</span>
                  )}
                </div>

                <div>
                  <p className="text-p2CopyMobile font-medium lg:text-p2CopyDesktop">
                    {file.name}
                  </p>
                  <p className="text-p2CopyMobile text-grau lg:text-p2CopyDesktop">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Status Indicator */}
                {file.status === 'uploading' && (
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full bg-black transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <span className="text-p2CopyMobile text-grau">
                      {file.progress}%
                    </span>
                  </div>
                )}

                {file.status === 'success' && (
                  <span className="text-green-600">&#10003;</span>
                )}

                {file.status === 'error' && (
                  <span
                    className="text-p2CopyMobile text-red-600"
                    title={file.error}
                  >
                    &#10005;
                  </span>
                )}

                {/* Remove Button */}
                {file.status !== 'uploading' && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.id)}
                    className="p-1 text-grau transition-colors hover:text-black"
                    aria-label={t('complaint.file_upload.remove')}
                  >
                    <svg
                      className="size-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
